import React, {useEffect, useMemo} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Grid,
  IconButton,
} from '@material-ui/core';
import { getQuoteProductSubscriptionOptionsApi } from 'api';
import { Delete as DeleteIcon, SettingsBackupRestore } from '@material-ui/icons';
import { getAlleDiscount } from 'pages/quotes/helpers';
import round from 'helpers/mathRound';
import { useStyles } from '../../styles';
import QuoteItemRowTotal from '../QuoteListings/QuoteItem/QuoteItemRowTotal';
import QuoteItemSubscription from '../QuoteListings/QuoteItem/QuoteItemSubscription';
import QuoteItemSavings from '../QuoteListings/QuoteItem/QuoteItemSavings';
import QuoteItemUnitPrice from '../QuoteListings/QuoteItem/QuoteItemUnitPrice';
import QuoteItemQuantity from '../QuoteListings/QuoteItem/QuoteItemQuantity';
import QuoteItemSelect from '../QuoteListings/QuoteItem/QuoteItemSelect';
import { handleSelectDataSource } from '../QuoteListings/utils/handleSelectDataSource';
import QuoteItemSubscriptionDiscount from '../QuoteListings/QuoteItem/QuoteItemSubscriptionDiscount';


const attributesList = ['savings', 'subscription_active', 'subscription_label', 'additional_subscription_price', 'quotepro_discounts'];

const SelectedPackageItem = ({
  selectedItem,
  productData,
  index,
  formikProps,
  setSelectedPackage
}) => {
  const {
    quoteProductsByGroup,
    subscriptionOptionsBySku
  } = useSelector((state: IReduxState) => state.quote);
  const {
    sku,
    selectedItemGroup
  } = selectedItem;

  const dispatch = useDispatch();
  const classes = useStyles();

  useEffect(() => {
    if (!subscriptionOptionsBySku?.[sku]) {
      getQuoteProductSubscriptionOptionsApi({dispatch, sku});
    }
  }, [sku]);

  const {
    values,
    setFieldValue
  } = formikProps;

  const handleDeleteRow = (index) => {
    const { items } = values;
    const updatedItems = items.filter((_, i) => i !== index);

    if (items.length === 1) {
      setSelectedPackage(null);
    } else {
      setFieldValue('items', updatedItems);
    }
  };

  const handleResetItem = (index) => {
    const { items } = values;
    const foundedItem = items[index];

    const selectedGroup = selectedItemGroup || values.selectedGroup;
    const selectedItem = quoteProductsByGroup?.[selectedGroup]?.find((product) => product.sku === foundedItem?.sku) || {};

    const { sku, custom_attributes = [], price = 0 } = selectedItem;

    const itemGroup = selectedItemGroup || (custom_attributes.find((attr) => attr.attribute_code === 'catalog_quotepro_groups')?.value?.toString().split(',')[0]);
    const isAlleSKU = sku.includes('bdsku');
    const itemSavings = isAlleSKU ? round(getAlleDiscount(selectedItem)) || 0 : round(custom_attributes.find((attr) => attr.attribute_code === 'savings')?.value) || 0;

    const itemAdditionalSubscriptionPrice = custom_attributes.find((attr) => attr.attribute_code === 'additional_subscription_price')?.value || 0;
    const isSubscriptionActive = custom_attributes.find((attr) => attr.attribute_code === 'subscription_active')?.value === '1' || false;
    const subscriptionOptions = productData?.product?.subscription_options;
    const [selectedOptionData] = subscriptionOptions;

    const { installment_price: initialInstallmentPrice = 0, adjustment_price: initialAdjustmentPrice = 0 } = Array.isArray(subscriptionOptionsBySku) ?
      (subscriptionOptions.find((option) => option.id === Number(selectedOptionData?.id)) || { installment_price: 0, adjustment_price: 0 }) :
      { installment_price: 0, adjustment_price: 0 };

    const newPrice = isSubscriptionActive && (initialInstallmentPrice || initialAdjustmentPrice) ?
      round(initialInstallmentPrice + initialAdjustmentPrice) + itemSavings :
      price + itemSavings;

    const product = {
      selectedItemGroup: itemGroup,
      sku,
      qty: 1,
      fixedPrice: newPrice,
      subscription_id: selectedOptionData?.id || '',
      discountType: '',
      subscriptionFrequency: '',
      additional_subscription_price: itemAdditionalSubscriptionPrice,
      savings: itemSavings,
      discount: '',
    };

    setFieldValue(`items[${index}]`, product);
  };

  const product = useMemo(() => {
    return quoteProductsByGroup?.[selectedItemGroup]?.find((el) => el.sku == sku);
  }, [selectedItemGroup, sku]);

  const selectedDataSource = useMemo(() => {
    return handleSelectDataSource(
      sku,
      true,
      productData?.product,
      subscriptionOptionsBySku,
      product?.custom_attributes || [],
      attributesList
    );
  }, [sku, productData?.product, subscriptionOptionsBySku]);

  return (
    <Grid container item xs={12} spacing={1} className={`${classes.quoteItem} mb-2`} >
      <QuoteItemSelect
        formik={formikProps}
        item={selectedItem}
        index={index}
      />

      <QuoteItemQuantity
        formik={formikProps}
        item={selectedItem}
        index={index}
        selectedDataSource={selectedDataSource}
      />

      <QuoteItemUnitPrice
        formik={formikProps}
        item={selectedItem}
        index={index}
        product={productData?.product}
        selectedDataSource={selectedDataSource}
      />

      <QuoteItemSavings
        formik={formikProps}
        item={selectedItem}
        index={index}
        selectedDataSource={selectedDataSource}
      />

      <QuoteItemSubscription
        formik={formikProps}
        item={selectedItem}
        index={index}
        selectedDataSource={selectedDataSource}
        product={productData?.product}
      />

      <QuoteItemSubscriptionDiscount
        formik={formikProps}
        item={selectedItem}
        index={index}
        selectedDataSource={selectedDataSource}
      />

      <QuoteItemRowTotal
        item={selectedItem}
        selectedDataSource={selectedDataSource}
      />

      <Grid item xs={12} lg="auto">
        <IconButton
          aria-label="restore"
          style={{padding: '8px 0'}}
          disabled={!selectedItem?.sku || selectedItem?.sku?.includes('bdsku')}
          onClick={() => handleResetItem(index)}
        >
          <SettingsBackupRestore />
        </IconButton>
        <IconButton
          aria-label="delete"
          style={{padding: '8px 0'}}
          onClick={() => handleDeleteRow(index)}
        >
          <DeleteIcon />
        </IconButton>
      </Grid>
    </Grid>
  );
};

export default SelectedPackageItem;
